import { useCallback, useEffect, useState } from "react";
import { ReduxStoreI } from "../store/redux-store";
import { useStoreActions, useStoreState } from "easy-peasy";
import { getCurrentAuth } from "./http-server";

const useBlocking = (ruleId: string) => {
  const [isBlockGame, setIsBlockGame] = useState(false);
  const { allRules } = useStoreState((state: ReduxStoreI) => state.rules);
  const { detectedLocation } = useStoreState(
    (state: ReduxStoreI) => state.rules
  );

  const auth = getCurrentAuth();

  const checkblockedorallowed = useCallback(
    (rule: any, detectedLocation: any) => {
      // Ensure rule has valid structure
      if (!rule || !rule.Type) {
        console.warn("Invalid rule structure");
        return { allowed: true, message: "" };
      }

      // Ensure Countries and States arrays exist
      const countries = Array.isArray(rule.Countries) ? rule.Countries : [];
      const states = Array.isArray(rule.States) ? rule.States : [];

      switch (rule.Type) {
        case "PERMIT_ALL":
          return { allowed: true, message: "" };

        case "RESTRICT_COUNTRIES_OR_STATES": {
          // If no countries or states are specified, don't restrict anything
          if (countries.length === 0 && states.length === 0) {
            return { allowed: true, message: "" };
          }

          // Check if user's country is in the restricted list
          const isRestrictedCountry = countries.some(
            (country: any) => country.Code === detectedLocation?.countryCode
          );

          // Check if user's state is in the restricted list
          const isRestrictedState = states.some(
            (state: any) =>
              state.Code === detectedLocation?.stateCode &&
              state.CountryCode === detectedLocation?.countryCode
          );

          // For RESTRICT rule, user is allowed if they are NOT in any restricted location
          return {
            allowed: !isRestrictedCountry && !isRestrictedState,
            message:
              isRestrictedCountry || isRestrictedState
                ? "This content is not available in your location."
                : "",
          };
        }

        case "PERMIT_COUNTRIES_OR_STATES": {
          // If no countries or states are specified, don't permit anything
          if (countries.length === 0 && states.length === 0) {
            return {
              allowed: false,
              message: "This content is not available in your location.",
            };
          }

          // Check if user's country is in the permitted list
          const isPermittedCountry = countries.some(
            (country: any) => country.Code === detectedLocation?.countryCode
          );

          // Check if user's state is in the permitted list
          const isPermittedState = states.some(
            (state: any) =>
              state.Code === detectedLocation?.stateCode &&
              state.CountryCode === detectedLocation?.countryCode
          );

          // For PERMIT rule, user is allowed if they are in any permitted location
          return {
            allowed: isPermittedCountry || isPermittedState,
            message:
              !isPermittedCountry && !isPermittedState
                ? "This content is not available in your location."
                : "",
          };
        }

        default:
          return { allowed: true, message: "" };
      }
    },
    []
  );

  const getRuleDetails = useCallback(async () => {
    try {
      // Early returns if no auth token or rule ID
      if (!auth?.AccessToken) return;
      if (!ruleId) return;

      // Check if rule exists in cache
      if (ruleId && Array.isArray(allRules) && allRules.length > 0) {
        const cachedRule = allRules.find((rule: any) => rule.ID === ruleId);

        if (cachedRule) {
          try {
            // Check if user is allowed based on cached rule
            const { allowed } = checkblockedorallowed(
              cachedRule,
              detectedLocation
            );

            if (!allowed) {
              setIsBlockGame(cachedRule);
              return;
            }
            return; // User is allowed, no need to block
          } catch (error) {
            console.error("Error processing cached rule:", error);
            // Continue to fetch from API if there's an error with cached rule
          }
        }
      }
    } catch (error) {
      console.error("Unexpected error in getRuleDetails:", error);
      // Default to allowing access if there's any unexpected error
    }
  }, [
    auth?.AccessToken,
    ruleId,
    allRules,
    checkblockedorallowed,
    detectedLocation,
    setIsBlockGame,
  ]);

  useEffect(() => {
    getRuleDetails();
  }, [getRuleDetails]);

  return { isBlockGame };
};

export default useBlocking;
